import { useState } from 'react';
import { Box, Typography, Paper, Tabs, Tab, Button } from '@mui/material';
import { MarkEmailRead as RejectionIcon, HowToReg as HiringIcon, LocalOffer as OfferIcon, Description as DocumentIcon, ForwardToInbox as DocumentsEmailIcon, Palette as BrandingIcon, Groups as ShortlistIcon, Badge as SenderIcon, Public as JobBoardIcon, AccountTree as PipelineIcon, PhoneDisabled as VetoesIcon, SmartToy as CaptationTplIcon, WhatsApp as WhatsappIcon, MenuBook as ManualIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import AtsRejectionEmailTab from './AtsRejectionEmailTab';
import AtsHiringEmailTab from './AtsHiringEmailTab';
import AtsOfferEmailTab from './AtsOfferEmailTab';
import AtsShortlistEmailTab from './AtsShortlistEmailTab';
import AtsDocumentCatalogTab from './AtsDocumentCatalogTab';
import AtsDocumentsEmailTab from './AtsDocumentsEmailTab';
import AtsBrandingTab from './AtsBrandingTab';
import AtsEmailSenderTab from './AtsEmailSenderTab';
import AtsJobBoardTab from './AtsJobBoardTab';
import AtsPipelineTemplatesTab from './AtsPipelineTemplatesTab';
import AtsCaptationVetoesTab from './AtsCaptationVetoesTab';
import AtsCaptationTemplatesTab from './AtsCaptationTemplatesTab';
import AtsWhatsappTab from './AtsWhatsappTab';
import useStore from '../../config/store';

// Manual de configuración en PDF (bucket público, igual que los términos y la
// política de privacidad). Para actualizarlo, reemplazar el archivo con el mismo nombre.
const ATS_CONFIG_MANUAL_URL = 'https://storage.googleapis.com/amandahr/Manual_configuracion_ATS.pdf';

/**
 * Pantalla de Configuración del ATS (réplica de la Configuración legacy,
 * con tabs propios). Por ahora: correo de descarte.
 */
const AtsConfigurationLayout = () => {
  const { t } = useTranslation();
  const [currentTab, setCurrentTab] = useState(0);
  const { user } = useStore.getState();
  // La Web de empleos configura a toda la organización → solo admin hacia arriba
  const isAdminPlus = ['admin', 'admin-grafton', 'super-admin'].includes(user?.rol);

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
          {t('ats.config.title')}
        </Typography>
        <Button
          variant="outlined"
          size="small"
          startIcon={<ManualIcon />}
          href={ATS_CONFIG_MANUAL_URL}
          target="_blank"
          rel="noopener noreferrer"
          sx={{ textTransform: 'none' }}
        >
          {t('ats.config.manualLink')}
        </Button>
      </Box>

      <Box sx={{ display: 'flex', gap: 3 }}>
        <Paper sx={{ width: 220, flexShrink: 0 }}>
          <Tabs
            orientation="vertical"
            value={currentTab}
            onChange={(e, v) => setCurrentTab(v)}
            sx={{ borderRight: 1, borderColor: 'divider' }}
          >
            <Tab
              icon={<RejectionIcon />}
              iconPosition="start"
              label={t('ats.config.tabs.rejectionEmail')}
              sx={{ justifyContent: 'flex-start', minHeight: 48 }}
            />
            <Tab
              icon={<HiringIcon />}
              iconPosition="start"
              label={t('ats.config.tabs.hiringEmail')}
              sx={{ justifyContent: 'flex-start', minHeight: 48 }}
            />
            <Tab
              icon={<OfferIcon />}
              iconPosition="start"
              label={t('ats.config.tabs.offerEmail')}
              sx={{ justifyContent: 'flex-start', minHeight: 48 }}
            />
            <Tab
              icon={<ShortlistIcon />}
              iconPosition="start"
              label={t('ats.config.tabs.shortlistEmail')}
              sx={{ justifyContent: 'flex-start', minHeight: 48 }}
            />
            <Tab
              icon={<DocumentIcon />}
              iconPosition="start"
              label={t('ats.config.tabs.documentCatalog')}
              sx={{ justifyContent: 'flex-start', minHeight: 48 }}
            />
            <Tab
              icon={<DocumentsEmailIcon />}
              iconPosition="start"
              label={t('ats.config.tabs.documentsEmail')}
              sx={{ justifyContent: 'flex-start', minHeight: 48 }}
            />
            <Tab
              icon={<BrandingIcon />}
              iconPosition="start"
              label={t('ats.config.tabs.branding')}
              sx={{ justifyContent: 'flex-start', minHeight: 48 }}
            />
            <Tab
              icon={<SenderIcon />}
              iconPosition="start"
              label={t('ats.config.tabs.emailSender')}
              sx={{ justifyContent: 'flex-start', minHeight: 48 }}
            />
            {isAdminPlus && (
              <Tab
                icon={<PipelineIcon />}
                iconPosition="start"
                label={t('ats.config.tabs.pipelineTemplates')}
                sx={{ justifyContent: 'flex-start', minHeight: 48 }}
              />
            )}
            {isAdminPlus && (
              <Tab
                icon={<CaptationTplIcon />}
                iconPosition="start"
                label={t('ats.config.tabs.captationTemplates')}
                sx={{ justifyContent: 'flex-start', minHeight: 48 }}
              />
            )}
            {isAdminPlus && (
              <Tab
                icon={<WhatsappIcon />}
                iconPosition="start"
                label={t('ats.config.tabs.whatsapp')}
                sx={{ justifyContent: 'flex-start', minHeight: 48 }}
              />
            )}
            {isAdminPlus && (
              <Tab
                icon={<JobBoardIcon />}
                iconPosition="start"
                label={t('ats.config.tabs.jobBoard')}
                sx={{ justifyContent: 'flex-start', minHeight: 48 }}
              />
            )}
            {isAdminPlus && (
              <Tab
                icon={<VetoesIcon />}
                iconPosition="start"
                label={t('ats.config.tabs.captationVetoes')}
                sx={{ justifyContent: 'flex-start', minHeight: 48 }}
              />
            )}
          </Tabs>
        </Paper>

        <Box sx={{ flexGrow: 1 }}>
          {currentTab === 0 && <AtsRejectionEmailTab />}
          {currentTab === 1 && <AtsHiringEmailTab />}
          {currentTab === 2 && <AtsOfferEmailTab />}
          {currentTab === 3 && <AtsShortlistEmailTab />}
          {currentTab === 4 && <AtsDocumentCatalogTab />}
          {currentTab === 5 && <AtsDocumentsEmailTab />}
          {currentTab === 6 && <AtsBrandingTab />}
          {currentTab === 7 && <AtsEmailSenderTab />}
          {currentTab === 8 && isAdminPlus && <AtsPipelineTemplatesTab />}
          {currentTab === 9 && isAdminPlus && <AtsCaptationTemplatesTab />}
          {currentTab === 10 && isAdminPlus && <AtsWhatsappTab />}
          {currentTab === 11 && isAdminPlus && <AtsJobBoardTab />}
          {currentTab === 12 && isAdminPlus && <AtsCaptationVetoesTab />}
        </Box>
      </Box>
    </Box>
  );
};

export default AtsConfigurationLayout;
